import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ExclamationTriangleIcon, MagnifyingGlassIcon, UserPlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { accessService } from '../services/accessService';
import { userService } from '../services/userService';
import { formatDate, isValidEmail, normalizeEmail } from '../lib/format';
import { AllowedEmail, AppUser } from '../types';
import ConfirmDialog from '../components/ConfirmDialog';
import { Avatar, EmptyState, ErrorNote, LoadingState, PageHeader, Spinner } from '../components/ui';

interface Row {
  email: string;
  user?: AppUser;
  invite?: AllowedEmail;
}

type PendingAction =
  | { kind: 'revoke'; row: Row }
  | { kind: 'admin'; row: Row; makeAdmin: boolean };

const AccessManagement: React.FC = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [invites, setInvites] = useState<AllowedEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [working, setWorking] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const [u, i] = await Promise.all([userService.getAllUsers(), accessService.list()]);
      setUsers(u);
      setInvites(i);
    } catch (error) {
      console.error('Error loading access data:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const map = new Map<string, Row>();
    invites.forEach((invite) => map.set(invite.email, { email: invite.email, invite }));
    users.forEach((u) => {
      const key = normalizeEmail(u.email || u.uid);
      map.set(key, { ...(map.get(key) || { email: key }), user: u });
    });
    const needle = search.trim().toLowerCase();
    return Array.from(map.values())
      .filter((r) => !needle || r.email.includes(needle) || r.user?.displayName.toLowerCase().includes(needle))
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [users, invites, search]);

  const uninvitedUsers = users.filter(
    (u) => !u.isAdmin && u.email && !invites.some((i) => i.email === normalizeEmail(u.email))
  );

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!isValidEmail(email)) {
      setInviteError(t('access.admin.invalidEmail'));
      return;
    }
    setInviteError('');
    setInviting(true);
    try {
      await accessService.invite(email, user.uid);
      toast.success(t('access.admin.invited', { email: normalizeEmail(email) }));
      setEmail('');
      await load();
    } catch (error) {
      console.error('Error inviting:', error);
      setInviteError(t('common.genericError'));
    } finally {
      setInviting(false);
    }
  };

  const inviteExisting = async (row: Row) => {
    if (!user) return;
    try {
      await accessService.invite(row.email, user.uid);
      toast.success(t('access.admin.invited', { email: row.email }));
      await load();
    } catch (error) {
      console.error(error);
      toast.error(t('common.genericError'));
    }
  };

  const runPending = async () => {
    if (!pending) return;
    setWorking(true);
    try {
      if (pending.kind === 'revoke') {
        await accessService.revoke(pending.row.email);
        toast.success(t('access.admin.revoked', { email: pending.row.email }));
      } else if (pending.row.user) {
        await userService.setAdminStatus(pending.row.user.uid, pending.makeAdmin);
        toast.success(pending.makeAdmin ? t('access.admin.madeAdmin') : t('access.admin.removedAdmin'));
      }
      setPending(null);
      await load();
    } catch (error) {
      console.error(error);
      toast.error(t('common.genericError'));
    } finally {
      setWorking(false);
    }
  };

  const migrate = async () => {
    if (!user) return;
    setMigrating(true);
    try {
      const summary = await accessService.migrateEverything(user.uid);
      toast.success(t('access.admin.migrated', { ...summary }), { duration: 8000 });
      await load();
    } catch (error) {
      console.error('Migration failed:', error);
      toast.error(t('access.admin.migrationFailed'));
    } finally {
      setMigrating(false);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-6">
      <PageHeader title={t('access.admin.title')} description={t('access.admin.subtitle')} />

      {loadError && <ErrorNote>{t('access.admin.loadFailed')}</ErrorNote>}

      {(uninvitedUsers.length > 0 || invites.length === 0) && (
        <section className="flex flex-col gap-4 rounded-xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/60 dark:bg-amber-950/30 sm:flex-row sm:items-start">
          <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="flex-1">
            <h2 className="font-semibold text-amber-900 dark:text-amber-200">{t('access.admin.migrationTitle')}</h2>
            <p className="mt-1 max-w-2xl text-sm text-amber-900/80 dark:text-amber-200/80">
              {t('access.admin.migrationText', { count: uninvitedUsers.length })}
            </p>
          </div>
          <button type="button" onClick={migrate} disabled={migrating} className="btn btn-primary shrink-0">
            {migrating && <Spinner className="h-4 w-4" />}
            {t('access.admin.migrationButton')}
          </button>
        </section>
      )}

      <section className="card p-5">
        <h2 className="font-semibold text-gray-900 dark:text-white">{t('access.admin.inviteTitle')}</h2>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">{t('access.admin.inviteText')}</p>
        <form onSubmit={handleInvite} className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nev@ceg.hu"
            aria-label={t('auth.email')}
            className="input sm:max-w-sm"
            required
          />
          <button type="submit" disabled={inviting || !email.trim()} className="btn btn-primary">
            <UserPlusIcon className="h-4 w-4" />
            {t('access.admin.inviteButton')}
          </button>
        </form>
        {inviteError && (
          <div className="mt-3">
            <ErrorNote>{inviteError}</ErrorNote>
          </div>
        )}
      </section>

      <section className="card">
        <div className="flex flex-col gap-3 px-5 pb-3 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {t('access.admin.people')} <span className="font-normal text-gray-400">({rows.length})</span>
          </h2>
          <div className="relative sm:w-64">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('access.admin.search')}
              aria-label={t('access.admin.search')}
              className="input h-9 pl-9"
            />
          </div>
        </div>

        {rows.length === 0 ? (
          <EmptyState icon={UsersIcon} title={t('access.admin.empty')} compact />
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {rows.map((row) => {
              const isSelf = row.user?.uid === user?.uid;
              const admin = row.user?.isAdmin === true;
              const status = admin ? 'admin' : row.invite ? (row.user ? 'active' : 'invited') : 'noAccess';
              const badgeClass = {
                admin: 'badge-brand',
                active: 'badge-ok',
                invited: 'badge-neutral',
                noAccess: 'badge-danger',
              }[status];
              return (
                <li key={row.email} className="flex flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <Avatar name={row.user?.displayName || row.email} photoURL={row.user?.photoURL} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {row.user?.displayName || row.email}
                        {isSelf && <span className="ml-1.5 text-xs font-normal text-gray-400">({t('access.admin.you')})</span>}
                      </p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {row.email}
                        {row.invite?.invitedAt && ` — ${t('access.admin.invitedOn', { date: formatDate(row.invite.invitedAt, language) })}`}
                        {row.user?.lastLoginAt && ` — ${t('access.admin.lastLogin', { date: formatDate(row.user.lastLoginAt, language) })}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <span className={`badge ${badgeClass}`}>{t(`access.admin.status.${status}`)}</span>
                    {!isSelf && row.user && (
                      <button
                        type="button"
                        onClick={() => setPending({ kind: 'admin', row, makeAdmin: !admin })}
                        className="btn btn-ghost btn-sm"
                      >
                        {admin ? t('access.admin.removeAdmin') : t('access.admin.makeAdmin')}
                      </button>
                    )}
                    {!isSelf && row.invite && (
                      <button type="button" onClick={() => setPending({ kind: 'revoke', row })} className="btn btn-danger-ghost btn-sm">
                        {t('access.admin.revoke')}
                      </button>
                    )}
                    {!row.invite && !admin && (
                      <button type="button" onClick={() => inviteExisting(row)} className="btn btn-secondary btn-sm">
                        {t('access.admin.grant')}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <ConfirmDialog
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={runPending}
        loading={working}
        type={pending?.kind === 'revoke' || (pending?.kind === 'admin' && !pending.makeAdmin) ? 'danger' : 'info'}
        title={
          pending?.kind === 'revoke'
            ? t('access.admin.revokeTitle')
            : pending?.kind === 'admin' && pending.makeAdmin
            ? t('access.admin.makeAdminTitle')
            : t('access.admin.removeAdminTitle')
        }
        message={
          pending?.kind === 'revoke'
            ? t('access.admin.revokeText', { email: pending.row.email })
            : pending?.kind === 'admin' && pending.makeAdmin
            ? t('access.admin.makeAdminText', { email: pending.row.email })
            : t('access.admin.removeAdminText', { email: pending?.row.email || '' })
        }
        confirmText={
          pending?.kind === 'revoke'
            ? t('access.admin.revoke')
            : pending?.kind === 'admin' && pending.makeAdmin
            ? t('access.admin.makeAdmin')
            : t('access.admin.removeAdmin')
        }
      />
    </div>
  );
};

export default AccessManagement;
